import React, { useEffect, useRef, useState } from 'react';
import '../../App.css';
import './MainHeroSection.css';

const INTRO_CONFIG = {
  logoDuration: 2000,
  beats: [
    { text: 'WHERE HUMOR MEETS SUCCESS', duration: 2500 },
  ],
};

function HeroSection() {
  const [phase, setPhase] = useState('logo'); // 'logo' | 'beats'
  const videoRef = useRef(null);

  // After logo shows, reveal both lines and KEEP them
  useEffect(() => {
    if (phase !== 'logo') return;
    const t = setTimeout(() => setPhase('beats'), INTRO_CONFIG.logoDuration);
    return () => clearTimeout(t);
  }, [phase]);

  // Ensure the background video actually autoplays on mobile.
  // Browsers only allow inline, muted autoplay — and sometimes React
  // doesn't sync the `muted` property to the DOM before first play,
  // which causes Safari/Chrome to show a play button instead.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Re-assert the autoplay-friendly properties at the DOM level.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const attemptPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* blocked — retry on first gesture below */ });
      }
    };

    attemptPlay();

    // Fallback for iOS Low Power Mode / stricter Androids: start the
    // video on the user's first gesture (tap, scroll, tap-anywhere).
    const controller = new AbortController();
    const resume = () => {
      attemptPlay();
      controller.abort();
    };
    const events = ['touchstart', 'click', 'scroll', 'keydown'];
    events.forEach((e) =>
      document.addEventListener(e, resume, {
        signal: controller.signal,
        passive: true,
      })
    );

    return () => controller.abort();
  }, []);

  return (
    <div className='hero-wrap'>
      <div className='hero-container'>
        {/* Background video */}
        <div className='slide-show'>
          <video
            ref={videoRef}
            className='background-video'
            autoPlay
            loop
            muted
            defaultMuted
            playsInline
            preload='auto'
            disablePictureInPicture
            disableRemotePlayback
            aria-hidden='true'
            tabIndex={-1}
          >
            <source src={process.env.PUBLIC_URL + '/assets/lrm-intro.mp4'} type='video/mp4' />
          </video>
          <div className='hero-overlay' />
        </div>

        {/* Centered content */}
        <div className='hero-center'>
          <img
            src={process.env.PUBLIC_URL + '/assets/lrm-menu.png'}
            alt='Logo'
            className={`hero-logo ${phase === 'logo' ? 'logo-in' : 'logo-out'}`}
          />

          <div className='beats-wrap'>
            {INTRO_CONFIG.beats.map((beat, i) => (
              <h1
                key={i}
                className={`beat ${phase === 'beats' ? 'beat-in' : 'beat-hidden'}`}
              >
                <span className='beat-text'>{beat.text}</span>
              </h1>
            ))}
          </div>
        </div>

        {/* Scroll cue */}
        <div className={`scroll-cue ${phase === 'beats' ? 'cue-in' : ''}`} aria-hidden='true'>
          <span className='cue-label'>Scroll</span>
          <span className='cue-line' />
        </div>
      </div>
    </div>
  );
}

export default HeroSection;